/**
 * Thin fetch wrapper for the Spring Boot API.
 *
 * In development Vite proxies /api to the backend (see vite.config.ts), and in
 * production Spring Boot serves this bundle, so the default relative base works in
 * both cases without configuration.
 *
 * Authentication is a session cookie, so requests must send credentials and echo the
 * CSRF token the server sets.
 */

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

/** Raised on the window when the server reports the session is no longer valid. */
export const SESSION_EXPIRED_EVENT = 'curlingcodex:session-expired';

/** Thrown for any non-2xx response, carrying the status so callers can branch on it. */
export class ApiError extends Error {
  readonly status: number;
  /** Field-level messages from a validation failure, keyed by field name. */
  readonly fieldErrors?: Record<string, string>;
  /** A machine-readable discriminator some ProblemDetail responses carry, e.g. so the client can tell two different 402s apart without matching on the message text. */
  readonly code?: string;

  constructor(message: string, status: number, fieldErrors?: Record<string, string>, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.fieldErrors = fieldErrors;
    this.code = code;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** Not signed in, or the session has expired. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** Signed in, but this account tier does not allow the action. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** A program's discoverable team count would exceed its coach headcount plus its purchased slots. */
  get needsAdditionalTeamSubscription(): boolean {
    return this.code === 'additional_team_subscription_required';
  }
}

const readCookie = (name: string): string | undefined =>
  document.cookie
    .split('; ')
    .find(entry => entry.startsWith(`${name}=`))
    ?.split('=')
    .slice(1)
    .join('=');

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string> | undefined),
  };

  // Spring publishes the token in a readable cookie; state-changing requests must
  // return it in this header or the request is rejected.
  if (MUTATING_METHODS.has(method)) {
    const token = readCookie('XSRF-TOKEN');
    if (token) {
      headers['X-XSRF-TOKEN'] = decodeURIComponent(token);
    }
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers,
      // Send and accept the session cookie.
      credentials: 'same-origin',
    });
  } catch (cause) {
    // fetch only rejects on network-level failure, which is worth distinguishing
    // from an API error so the UI can say "server unreachable".
    throw new ApiError(`Could not reach the server: ${String(cause)}`, 0);
  }

  if (!response.ok) {
    const failure = await describeFailure(response);

    // Let the app clear its cached user when the session goes away mid-use. The
    // sign-in probe opts out, since a 401 there is the expected answer.
    if (response.status === 401 && !path.startsWith('/auth/')) {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }

    throw new ApiError(failure.detail, response.status, failure.fieldErrors, failure.code);
  }

  // 204, and any other empty body, has nothing to parse.
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  const text = await response.text();
  return text ? (JSON.parse(text) as T) : (undefined as T);
}

/** Pulls the most useful message out of a ProblemDetail body, falling back to the status. */
async function describeFailure(
  response: Response
): Promise<{ detail: string; fieldErrors?: Record<string, string>; code?: string }> {
  try {
    const body = await response.json();
    return {
      detail: body?.detail ?? body?.title ?? `${response.status} ${response.statusText}`,
      fieldErrors: body?.fieldErrors,
      code: body?.code,
    };
  } catch {
    return { detail: `${response.status} ${response.statusText}` };
  }
}

/** Path segments may contain spaces and slashes (club and player names), so encode them. */
export const seg = (value: string): string => encodeURIComponent(value);

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T = void>(path: string) => request<T>(path, { method: 'DELETE' }),
};
